/**
 * Email validation troubleshooting page: validate email configuration for the current store.
 * Single-button tool that calls the backend API and displays the validation result.
 */

import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';

import CurrentPortalStore from '../../../../stores/current-portal-store';
import { Button } from '../../../../components/inputs/button/button';
import JsonFormatter from '../../../../components/json-formatter/json-formatter';
import { FONT_COLOR, FONT_VARIANT } from '../../../../components/fonts/font-props';
import FontBody2 from '../../../../components/fonts/font-body2/font-body2';
import LinearLoader from '../../../../components/loaders/linear-loader/linear-loader';

import TroubleshootingStore from '../../troubleshooting-page-store';

import './email-validation-page.scss';

const EmailValidationPage: React.FC = observer(() => {
    const [shopifyResponse, setShopifyResponse] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        CurrentPortalStore.load();
    }, []);

    const portalId = CurrentPortalStore.currentPortal.id;
    const canValidate = !!portalId;

    const __shopifyResponse = TroubleshootingStore.shopifyResponse;
    useEffect(() => {
        setShopifyResponse(toJS(TroubleshootingStore.shopifyResponse));
        setIsLoading(false);
    }, [__shopifyResponse]);

    const performValidation = async () => {
        if (!portalId) return;
        setIsLoading(true);
        try {
            await TroubleshootingStore.validateEmail(portalId);
        } finally {
            setIsLoading(false);
        }
    };

    const hasResponse = shopifyResponse != null && Object.keys(shopifyResponse).length > 0;

    return (
        <div className="email-validation-page">
            <div className="card">
                <h4>Email Validation</h4>
                <p className="description">
                    <FontBody2 color={FONT_COLOR.NEUTRAL10}>
                        {canValidate
                            ? 'Validate email configuration for the current store.'
                            : 'No store selected. Switch to a store to validate email configuration.'}
                    </FontBody2>
                </p>
                <div className="troubleshooting-buttons-container">
                    <Button
                        text="Validate email"
                        disabled={!canValidate || isLoading}
                        onClick={performValidation}
                    />
                </div>
            </div>

            {isLoading && (
                <div className="card">
                    <FontBody2 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                        Validating email configuration. Please wait...
                        <LinearLoader />
                    </FontBody2>
                </div>
            )}

            {!isLoading && hasResponse && (
                <div className="card results-card">
                    <h4>Results</h4>
                    <JsonFormatter expansionLevel={2} json={shopifyResponse} />
                </div>
            )}
        </div>
    );
});

export default EmailValidationPage;
